import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { fetchAllSnapshots } from "@/lib/api";
import { regionHealth, healthBand, toneColor } from "@/lib/laymen";
import AddressSearch from "@/components/AddressSearch";

/**
 * Minimal Leaflet wrapper — plain vanilla Leaflet inside React.
 * We deliberately avoid react-leaflet because its MapContainer clashes with
 * React 18 StrictMode's double-mount cycle ("Map container is already initialized").
 */
export default function RegionMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  // Load snapshot data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchAllSnapshots();
        if (!cancelled) setData(d);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const points = useMemo(() => {
    if (!data) return [];
    const snapMap = {};
    (data.snapshots || []).forEach((s) => { snapMap[s.region_id] = s; });
    return (data.regions || []).map((r) => {
      const m = snapMap[r.id]?.metrics;
      const score = regionHealth(m);
      const tone = toneColor(healthBand(score).level);
      return {
        id: r.id,
        lat: r.centroid[1],
        lon: r.centroid[0],
        label: r.label,
        desc: r.description,
        bl: r.bundesland,
        country: r.country,
        crop: r.crop,
        ndvi: m?.ndvi_current,
        soil: m?.soil_moisture_current,
        yieldIdx: m?.yield_forecast_index,
        score,
        color: tone.dot,
        bandLabel: healthBand(score).label,
      };
    });
  }, [data]);

  // Initialise the map once.
  useEffect(() => {
    if (loading || !containerRef.current) return;
    // Guard against StrictMode double-invocation.
    if (mapRef.current) return;
    // In case a previous render left a stale _leaflet_id on the node, reset it.
    if (containerRef.current._leaflet_id) {
      delete containerRef.current._leaflet_id;
    }
    const map = L.map(containerRef.current, {
      center: [48.5, 12.5],
      zoom: 5,
      minZoom: 4,
      scrollWheelZoom: true,
      attributionControl: true,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "© OpenStreetMap-Mitwirkende",
      maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    return () => {
      try {
        map.remove();
      } catch { /* noop */ }
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [loading]);

  // Re-render markers when the point set changes.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    points.forEach((p) => {
      const marker = L.circleMarker([p.lat, p.lon], {
        radius: Math.max(7, Math.min(18, p.score / 6 + 6)),
        color: p.color,
        fillColor: p.color,
        fillOpacity: 0.55,
        weight: 2,
      });
      marker.bindPopup(
        `<div style="font-family:system-ui,sans-serif;min-width:200px;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#8A8A8A;">
            ${p.country} · ${p.bl}
          </div>
          <div style="font-size:15px;font-weight:500;margin:4px 0 2px;">${p.label}</div>
          <div style="font-size:12px;color:#5C5C5C;margin-bottom:6px;">${p.desc || ""}</div>
          <table style="font-size:12px;border-collapse:collapse;width:100%;">
            <tr><td style="color:#8A8A8A;padding:2px 6px 2px 0;">Score</td>
                <td style="font-family:monospace;color:${p.color};">${p.score} (${p.bandLabel})</td></tr>
            <tr><td style="color:#8A8A8A;padding:2px 6px 2px 0;">NDVI</td>
                <td style="font-family:monospace;">${p.ndvi?.toFixed?.(2) ?? "–"}</td></tr>
            <tr><td style="color:#8A8A8A;padding:2px 6px 2px 0;">Boden m³/m³</td>
                <td style="font-family:monospace;">${p.soil?.toFixed?.(2) ?? "–"}</td></tr>
            <tr><td style="color:#8A8A8A;padding:2px 6px 2px 0;">Ertrag /100</td>
                <td style="font-family:monospace;">${p.yieldIdx?.toFixed?.(0) ?? "–"}</td></tr>
            <tr><td style="color:#8A8A8A;padding:2px 6px 2px 0;">Kultur</td>
                <td style="font-family:monospace;">${p.crop}</td></tr>
          </table>
        </div>`
      );
      marker.addTo(layer);
    });
  }, [points]);

  return (
    <div className="space-y-4" data-testid="region-map-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eid-label">Ansicht · Karte</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Regionen-Karte</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Alle {points.length} beobachteten Regionen in Österreich und Deutschland.
            Größe und Farbe der Punkte spiegeln den Ökosystem-Score wider.
          </p>
        </div>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
          data-testid="map-back-to-dashboard"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </header>

      <div className="max-w-md" data-testid="map-address-wrap">
        <AddressSearch
          onPick={(lon, lat, label) => {
            const map = mapRef.current;
            if (!map) return;
            map.flyTo([lat, lon], 11, { duration: 0.8 });
            const tempMarker = L.circleMarker([lat, lon], {
              radius: 10, color: "#FF5500", fillColor: "#FF5500", fillOpacity: 0.3, weight: 2,
            }).bindTooltip(label.split(",")[0], { permanent: true, direction: "top" })
              .addTo(mapRef.current);
            setTimeout(() => {
              try { mapRef.current?.removeLayer(tempMarker); } catch { /* noop */ }
            }, 8000);
          }}
          testid="map-address"
        />
      </div>

      <div className="eid-card overflow-hidden" style={{ height: 620 }}>
        {loading ? (
          <div className="p-8 text-sm text-zinc-400">Karte wird geladen…</div>
        ) : (
          <div
            ref={containerRef}
            style={{ height: "100%", width: "100%" }}
            data-testid="leaflet-container"
          />
        )}
      </div>

      <div className="eid-card p-4 flex flex-wrap gap-3 text-xs" data-testid="map-legend">
        <span className="font-mono uppercase tracking-wider text-zinc-400">Legende:</span>
        <Legend color="#00FA9A" label="Sehr gut (≥75)" />
        <Legend color="#7EE8B0" label="Stabil (55–74)" />
        <Legend color="#FFC24B" label="Unter Druck (35–54)" />
        <Legend color="#FF5500" label="Kritisch (<35)" />
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
      <span className="text-white">{label}</span>
    </span>
  );
}
